/**
 * Funnel measurement: anonymous daily counters only.
 *
 * We never store who did something. Each event adds 1 to a (day, step, tag) counter, so there is nothing to
 * identify a visitor and nothing to erase. "Tool users" is not counted here at all: it comes from the existing
 * usage_log table, which already holds what the free-use limit needs.
 */

/** The steps this module counts. Tool use is read from usage_log instead. */
export const FUNNEL_STEPS = ["visit", "paywall_view", "checkout_start", "paid"] as const;
export type FunnelStep = (typeof FUNNEL_STEPS)[number];

/** Steps a browser may report itself. The two money steps are only ever recorded on the server. */
export const BROWSER_STEPS = ["visit", "paywall_view"] as const;

/** Where a visit came from, in five coarse buckets. The referrer itself is never kept. */
export const VISIT_SOURCES = ["direct", "search", "social", "ai", "other_site"] as const;
export type VisitSource = (typeof VISIT_SOURCES)[number];

/** Which product a paywall view, checkout start or payment was for. */
export const PRODUCT_TAGS = ["pro", "premium_apps", "workflow", "event_suite", "api", "book_maker", "team", "other"] as const;
export type ProductTag = (typeof PRODUCT_TAGS)[number];

/**
 * Traffic that is kept out of the funnel numbers but still counted, so the report can show how much was excluded.
 * Stored as step "excluded" with one of these reasons as the tag. Nothing identifies the visitor.
 */
export const EXCLUDED_STEP = "excluded";
export const EXCLUDE_REASONS = ["bot", "admin", "internal", "automation", "no_language"] as const;
export type ExcludeReason = (typeof EXCLUDE_REASONS)[number];

export const ALL_TAGS: readonly string[] = [...VISIT_SOURCES, ...PRODUCT_TAGS, ...EXCLUDE_REASONS];

const SEARCH_HOSTS = ["google.", "bing.com", "duckduckgo.com", "yahoo.", "baidu.com", "yandex.", "ecosia.org", "brave.com", "startpage.com", "naver.com", "seznam.cz"];
const SOCIAL_HOSTS = ["facebook.com", "instagram.com", "t.co", "x.com", "twitter.com", "linkedin.com", "lnkd.in", "reddit.com", "pinterest.", "youtube.com", "youtu.be", "tiktok.com", "whatsapp.com", "t.me", "telegram.org", "news.ycombinator.com", "medium.com", "wordpress.com", "blogspot.com", "hubspot.com"];
const AI_HOSTS = ["chatgpt.com", "chat.openai.com", "openai.com", "perplexity.ai", "claude.ai", "gemini.google.com", "copilot.microsoft.com", "you.com", "phind.com"];

function hostMatches(host: string, list: string[]): boolean {
  return list.some((h) => (h.endsWith(".") ? host.includes(h) : host === h || host.endsWith("." + h)));
}

/** Sorts a document.referrer into one of five buckets. Pure, so it can be tested. */
export function classifyReferrer(referrer: string | undefined | null, ownHost: string): VisitSource {
  if (!referrer) return "direct";
  let host = "";
  try {
    host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "direct";
  }
  const own = ownHost.toLowerCase().replace(/^www\./, "");
  if (!host || host === own) return "direct";
  // AI hosts first: gemini.google.com would otherwise match "google." as a search engine.
  if (hostMatches(host, AI_HOSTS)) return "ai";
  if (hostMatches(host, SEARCH_HOSTS)) return "search";
  if (hostMatches(host, SOCIAL_HOSTS)) return "social";
  return "other_site";
}

/** Crawlers and monitors should not count as visitors. */
export function isBotUserAgent(ua: string | undefined | null): boolean {
  if (!ua) return true;
  return /bot|crawl|spider|slurp|facebookexternalhit|embedly|preview|monitor|uptime|headless|lighthouse|pingdom|curl|wget|python-requests|go-http-client|axios|node-fetch/i.test(ua);
}

export interface ExclusionSignals {
  userAgent?: string | null;
  acceptLanguage?: string | null;
  isAdmin?: boolean;
  /** True when the browser says it is our own device (internal-traffic flag) -- a boolean, never an identifier. */
  internalFlag?: boolean;
  /** True when the browser reports it is driven by automation (navigator.webdriver, headless). */
  automationFlag?: boolean;
}

/**
 * Why a funnel hit should not count as a real visitor, or null when it should. Pure, so it can be tested.
 * Order matters: the most specific, most certain reason wins. Datacenter networks are not detectable here (no
 * network-owner data is available to the server), and nothing is blocked by country: this only decides what the
 * report counts, never who may use the site.
 */
export function classifyExclusion(s: ExclusionSignals): ExcludeReason | null {
  if (s.isAdmin) return "admin";
  if (s.internalFlag) return "internal";
  if (isBotUserAgent(s.userAgent)) return "bot";
  if (s.automationFlag) return "automation";
  if (!s.acceptLanguage || !s.acceptLanguage.trim()) return "no_language";
  return null;
}

/** The product tag for a Stripe Checkout session, from the metadata we already put on every session. */
export function productTagFromMetadata(metadata: Record<string, string | null | undefined> | null | undefined): ProductTag {
  const type = metadata?.type;
  switch (type) {
    case undefined:
    case null:
    case "":
      return "pro";
    case "premium_apps":
      return "premium_apps";
    case "workflow_product":
      return "workflow";
    case "event_suite":
      return "event_suite";
    case "api":
      return "api";
    case "book_maker":
      return "book_maker";
    case "team":
    case "org_block":
      return "team";
    default:
      return "other";
  }
}

export interface FunnelRow {
  day: string;
  step: string;
  tag: string;
  n: number;
}

export interface FunnelTotals {
  visits: number;
  toolUsers: number;
  paywallViews: number;
  checkoutStarts: number;
  paid: number;
  /** Hits kept out of the numbers above (bots, our own traffic, automation), in total and by reason. */
  excluded: number;
  excludedBy: Record<string, number>;
}

/** step-to-step conversion as a whole-number percent string, or "-" when there is nothing to divide by. */
export function pct(part: number, whole: number): string {
  if (!whole) return "-";
  const p = (part / whole) * 100;
  return `${p >= 10 ? Math.round(p) : Math.round(p * 10) / 10}%`;
}

/** Adds up the counter rows into one total per step. */
export function totalsFromRows(rows: FunnelRow[], toolUsers: number): FunnelTotals {
  const sum = (step: string) => rows.filter((r) => r.step === step).reduce((n, r) => n + r.n, 0);
  const excludedBy: Record<string, number> = {};
  for (const r of rows) if (r.step === EXCLUDED_STEP) excludedBy[r.tag] = (excludedBy[r.tag] ?? 0) + r.n;
  const excluded = Object.values(excludedBy).reduce((a, b) => a + b, 0);
  return { visits: sum("visit"), toolUsers, paywallViews: sum("paywall_view"), checkoutStarts: sum("checkout_start"), paid: sum("paid"), excluded, excludedBy };
}
